import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import FarmerForm from '../../components/admin/farmer/FarmerForm';
import FarmerDataForm from '../../components/admin/farmer/FarmerDataForm';
import { farmerApi } from '../../services/api';
import LoadingIndicator from '../../components/common/LoadingIndicator';

const FarmerFormPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [farmer, setFarmer] = useState(null);
  const [loading, setLoading] = useState(id ? true : false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Fetch farmer data if editing an existing farmer
  useEffect(() => {
    if (id) {
      const fetchFarmer = async () => {
        try {
          const data = await farmerApi.getFarmer(id);
          setFarmer(data);
          setError(null);
        } catch (err) {
          console.error('Error fetching farmer:', err);
          setError('Failed to load farmer data. Please try again.');
        } finally {
          setLoading(false);
        }
      };

      fetchFarmer();
    }
  }, [id]);

  const handleSubmit = async (formData) => {
    setIsSubmitting(true);
    setError(null);

    try {
      if (id) {
        await farmerApi.updateFarmer(id, formData);
      } else {
        await farmerApi.createFarmer(formData);
      }
      // Navigate back to the farmer list page after successful submission
      navigate('/admin/farmer-database');
    } catch (err) {
      setError('Failed to save farmer. Please try again.');
      console.error('Error saving farmer:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    // Navigate back to the farmer list page
    navigate('/admin/farmer-database');
  };

  return (
    <div style={styles.container}>
      <div style={styles.pageHeader}>
        <div style={styles.headerTop}>
          <div style={styles.titleContainer}>
            <span style={styles.titleIcon}>🌾</span>
            <h1 style={styles.title}>
              {id ? 'Edit Farmer' : 'Add New Farmer'}
            </h1>
          </div>
          <button
            onClick={handleCancel}
            style={styles.closeButton}
            title="Close"
          >
            ✕
          </button>
        </div>
        <p style={styles.subtitle}>
          {id
            ? 'Update farmer information in the database'
            : 'Register a new farmer in the database'
          }
        </p>
      </div>

      <div style={styles.content}>
        {loading ? (
          <div style={styles.loadingContainer}>
            <LoadingIndicator />
            <p>Loading farmer data...</p>
          </div>
        ) : error ? (
          <div style={styles.errorContainer}>
            <div style={styles.errorIcon}>⚠️</div>
            <p style={styles.errorText}>{error}</p>
            <button onClick={() => navigate('/admin/farmer-database')} style={styles.backButton}>
              Back to Farmer List
            </button>
          </div>
        ) : (
          <>
            {error && (
              <div style={styles.errorMessage}>
                {error}
              </div>
            )}
            <FarmerForm
              farmer={farmer}
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              isSubmitting={isSubmitting}
            />
          </>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    padding: '1rem',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  pageHeader: {
    marginBottom: '2rem',
  },
  headerTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.5rem',
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  titleIcon: {
    fontSize: '2rem',
    marginRight: '0.75rem',
  },
  title: {
    fontSize: '1.8rem',
    fontWeight: 'bold',
    margin: 0,
    color: '#2a4a36', // Dark green
  },
  subtitle: {
    fontSize: '1rem',
    color: '#666',
    margin: 0,
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '1.5rem',
    color: '#666',
    cursor: 'pointer',
    padding: '0.5rem',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: '#f0f0f0',
    },
  },
  content: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    padding: '2rem',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '3rem',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '3rem',
    textAlign: 'center',
  },
  errorIcon: {
    fontSize: '3rem',
    marginBottom: '1rem',
  },
  errorText: {
    fontSize: '1.1rem',
    color: '#d32f2f',
    marginBottom: '1.5rem',
  },
  backButton: {
    padding: '0.5rem 1rem',
    backgroundColor: '#4caf50',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.9rem',
  },
  errorMessage: {
    backgroundColor: '#ffebee',
    color: '#d32f2f',
    padding: '1rem',
    borderRadius: '4px',
    marginBottom: '1rem',
    borderLeft: '4px solid #d32f2f',
  },
};

export default FarmerFormPage;
